import { PageController } from '@client/components/_core/layout/PageController';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { PERMISSIONS } from '@shared/permissions/permissions';
import { createFileRoute } from '@tanstack/react-router';
import { Typography } from 'antd';
import { FileSearch, Globe, Zap } from 'lucide-react';
import { useMemo } from 'react';
import { QuickActions } from './components/QuickActions';
import type { QuickAction } from './components/types';

export const Route = createFileRoute('/console/$orgId/tools-only-home-page')({
    staticData: {
        roles: ['user', 'shared_edit_user', 'org_admin'],
        ui: {
            hideTitle: true,
            widthVariant: 'default',
        },
        breadcrumb: {
            title: 'Home',
            inactive: true,
        },
    },
    component: ToolsOnlyHomePage,
});

function ToolsOnlyHomePage() {
    const user = useUser();
    const orgId = useOrgId();
    const navigate = useNavigate();
    const { can } = usePermissions();
    const complianceWebsiteChecker = useFeatureFlag('compliance-website-checker');

    const canAccessAutoDetect = can(PERMISSIONS.MESSAGE_CLASSIFICATION_ACCESS);
    const canAccessWebsiteChecker = complianceWebsiteChecker;
    const canAccessDocumentAnalyzer = can(PERMISSIONS.DOCUMENT_ANALYZER_USE);

    const quickActions: QuickAction[] = useMemo(
        () => [
            {
                label: 'Auto-detect',
                icon: Zap,
                onClick: () => navigate({ to: '/console/$orgId/tools/auto-detect/inbox', params: { orgId } }),
                type: 'primary',
                visible: canAccessAutoDetect,
            },
            {
                label: 'Website Checker',
                icon: Globe,
                onClick: () => navigate({ to: '/console/$orgId/tools/website-checker', params: { orgId } }),
                type: 'secondary',
                visible: canAccessWebsiteChecker,
            },
            {
                label: 'Document Analyzer',
                icon: FileSearch,
                onClick: () => navigate({ to: '/console/$orgId/tools/document-analyzer', params: { orgId } }),
                type: 'secondary',
                visible: canAccessDocumentAnalyzer,
            },
        ],
        [navigate, orgId, canAccessAutoDetect, canAccessWebsiteChecker, canAccessDocumentAnalyzer],
    );

    return (
        <PageController route={Route}>
            {/* Welcome Section */}
            <div className="mb-6">
                <Typography.Title level={2} className="mb-2">
                    Hello, {user.fullName}!
                </Typography.Title>
                <Typography.Text className="text-base text-muted-foreground">
                    What would you like to do today?
                </Typography.Text>
            </div>

            {/* Quick Actions */}
            <QuickActions actions={quickActions} />
        </PageController>
    );
}
